import { Box, CssBaseline, InitColorSchemeScript, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NotificationsProvider } from '@toolpad/core';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import GoogleAnalyticsTracker from './analytics/GoogleAnalyticsTracker';
import './App.css';
import PrimaryBar from './Components/AppBars/PrimaryBar/PrimaryBar';
import { AppProvider } from './Components/AppProvider';
import DialogManger from './Components/Dialogs/DialogManager';
import ConnectedDrawer from './Components/Drawers/ConnectedDrawer';
import ConnectionDrawer from './Components/Drawers/ConnectionDrawer';
import { SettingsDrawer } from './Components/Drawers/Settings';
import { ActiveEditorProvider } from './Components/Editor/active-editor-hooks';
import AppSnackbars from './Components/Snackbars/AppSnackbars';
import ProgramStateSnackbar from './Components/Snackbars/ProgramStateSnackbar';
import ProgramDiagnosticsSync from './Components/ProgramDiagnosticsSync';
import { deployment } from './deployment';
import { FileSystem } from './fs/fs';
import { FileSystemProvider } from './fs/fs-hooks';
import { createHost } from './fs/host';
import i18n from './i18n';
import { Logging } from './logging/logging';
import { LoggingProvider } from './logging/LoggingProvider';
import { fetchMicroPython } from './micropython/micropython';
import BpsRoutes from './Pages/BpsRoutes';
import store, { persistor } from './redux/Store';
import SessionSettingsProvider from './settings/session-settings';
import SettingsProvider from './settings/SettingsProvider';
import bpsTheme from './theme/theme';

export interface DeploymentConfig {
  logging: Logging;
}

function App() {
  const logging = deployment.logging;

  const host = createHost(logging);
  const fs = new FileSystem(logging, host, fetchMicroPython);

  // If this fails then we retry on access.
  fs.initializeInBackground();

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <NotificationsProvider>
              <InitColorSchemeScript attribute="class" defaultMode="system" />
              <ThemeProvider theme={bpsTheme} defaultMode="system" disableTransitionOnChange>
                <CssBaseline />
                <SettingsProvider>
                  <SessionSettingsProvider>
                    <FileSystemProvider value={fs}>
                      <LoggingProvider>
                        <Router>
                          <GoogleAnalyticsTracker />
                          <AppProvider>
                            <ActiveEditorProvider>
                              <ProgramDiagnosticsSync />
                              <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                                <PrimaryBar />
                                <BpsRoutes />
                                <ProgramStateSnackbar />
                                <AppSnackbars />
                                <ConnectionDrawer />
                                <ConnectedDrawer />
                                <SettingsDrawer />
                                <DialogManger />
                              </Box>
                            </ActiveEditorProvider>
                          </AppProvider>
                        </Router>
                      </LoggingProvider>
                    </FileSystemProvider>
                  </SessionSettingsProvider>
                </SettingsProvider>
              </ThemeProvider>
            </NotificationsProvider>
          </I18nextProvider>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
